import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { SERVICES, PROJECTS, STATS, IMAGES } from "../data/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0" aria-hidden="true">
        <img src={IMAGES.hero} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/60" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-16 pt-40 md:px-12 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="font-mono text-sm font-bold uppercase tracking-[0.3em] text-ember"
          data-testid="hero-overline"
        >
          Prott Bauunternehmung — Offenbach am Main
        </motion.p>

        <MaskedLines
          lines={["WIR BAUEN.", "SEIT 30 JAHREN.", "FÜR GENERATIONEN."]}
          className="mt-8"
          lineClassName="font-display text-[13vw] font-black uppercase leading-[0.95] tracking-tighter text-bone md:text-[8.5vw]"
        />

        <div className="mt-12 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="max-w-md text-base leading-relaxed text-ash md:text-lg"
            data-testid="hero-subtext"
          >
            Familiengeführt. Verlässlich. Von der Alten Oper Frankfurt bis zum Wohnquartier in Langen — Rohbau, Umbau, Sanierung und Gartenbau aus einer Hand.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="flex flex-wrap gap-4"
          >
            <Link
              to="/kontakt"
              data-testid="hero-cta-contact"
              className="group flex items-center gap-3 bg-ember px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-bone"
            >
              Projekt anfragen
              <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/projekte"
              data-testid="hero-cta-projects"
              className="group flex items-center gap-3 border border-bone/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:border-ember hover:text-ember"
            >
              Referenzen
              <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 1 }}
        className="relative z-10 border-t border-bone/10"
        data-testid="hero-stats"
      >
        <div className="mx-auto grid max-w-[1600px] grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <div key={s.label} className={`px-6 py-8 md:px-12 ${i > 0 ? "border-l border-bone/10" : ""} ${i >= 2 ? "border-t border-bone/10 lg:border-t-0" : ""}`}>
              <p className="font-display text-3xl font-bold text-bone md:text-4xl">
                {s.value}<span className="text-ember">{s.suffix}</span>
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ash">{s.label}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

const ServicesBento = () => (
  <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32" data-testid="home-services">
    <Reveal>
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Leistungen</p>
          <h2 className="mt-6 max-w-2xl font-display text-4xl font-bold uppercase leading-none tracking-tighter text-bone md:text-6xl">
            Vier Gewerke.<br />Ein Versprechen.
          </h2>
        </div>
        <Link to="/leistungen" data-testid="services-all-link" className="group flex w-fit items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:text-ember">
          Alle Leistungen
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </Reveal>

    <div className="mt-16 grid gap-px border border-line bg-line md:grid-cols-2">
      {SERVICES.map((s, i) => (
        <Reveal key={s.slug} delay={i * 0.08}>
          <Link
            to={`/leistungen#${s.slug}`}
            data-testid={`service-card-${s.slug}`}
            className="group relative block h-full overflow-hidden bg-ink p-8 transition-colors duration-500 md:p-12"
          >
            <div className="absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100" aria-hidden="true">
              <img src={s.image} alt="" className="h-full w-full object-cover opacity-25" />
            </div>
            <div className="relative z-10 flex h-full flex-col justify-between gap-16">
              <div className="flex items-start justify-between">
                <span className="font-mono text-sm text-ember">{s.nr}</span>
                <ArrowUpRight size={22} className="text-ash transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-ember" />
              </div>
              <div>
                <h3 className="font-display text-3xl font-bold uppercase tracking-tight text-bone md:text-4xl">{s.title}</h3>
                <p className="mt-4 max-w-sm text-sm leading-relaxed text-ash">{s.claim}</p>
              </div>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const ProjectsTeaser = () => {
  const featured = PROJECTS.filter((p) => p.highlight);
  return (
    <section className="border-y border-line bg-coal" data-testid="home-projects-teaser">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Ausgewählte Referenzen</p>
          <h2 className="mt-6 font-display text-4xl font-bold uppercase tracking-tighter text-bone md:text-6xl">
            Gebaute Handschrift
          </h2>
        </Reveal>

        <div className="mt-16">
          {featured.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06}>
              <Link
                to="/projekte"
                data-testid={`teaser-project-${i}`}
                className="group flex flex-col gap-3 border-t border-line py-8 transition-colors duration-300 last:border-b hover:bg-ink/60 md:flex-row md:items-center md:justify-between md:px-6"
              >
                <div className="flex items-baseline gap-6">
                  <span className="font-mono text-xs text-ember">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-2xl font-semibold uppercase tracking-tight text-bone transition-colors duration-300 group-hover:text-ember md:text-4xl">
                    {p.name}
                  </h3>
                </div>
                <div className="flex items-center gap-6 pl-12 md:pl-0">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash">{p.category}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash">{p.location}</span>
                  <ArrowUpRight size={18} className="text-ash transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-ember" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <Link
            to="/projekte"
            data-testid="projects-all-link"
            className="group mt-12 inline-flex items-center gap-3 border border-bone/30 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:border-ember hover:text-ember"
          >
            Alle 16 Bauvorhaben
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const AboutTeaser = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32" data-testid="home-about-teaser">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Über uns</p>
          <h2 className="mt-6 font-display text-4xl font-bold uppercase leading-none tracking-tighter text-bone md:text-5xl">
            Eine Familie.<br />Drei Jahrzehnte.<br />Ein Anspruch.
          </h2>
          <p className="mt-8 max-w-lg text-base leading-relaxed text-ash">
            Seit über 30 Jahren bauen wir im Raum Frankfurt — verwurzelt in Offenbach am Main. Als Familienunternehmen stehen wir mit unserem Namen für jedes Fundament, jede Sanierung und jede Außenanlage. Persönlich, direkt, ohne Umwege.
          </p>
          <Link
            to="/ueber-uns"
            data-testid="about-teaser-link"
            className="group mt-10 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-ember"
          >
            Unsere Geschichte
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative overflow-hidden border border-line">
            <motion.img
              style={{ y }}
              src={IMAGES.frankfurt}
              alt="Skyline von Frankfurt am Main bei Nacht"
              className="h-[420px] w-full scale-125 object-cover md:h-[520px]"
            />
            <div className="absolute bottom-0 left-0 border-t border-r border-line bg-ink px-6 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash">Raum Frankfurt — Heimat seit 30 Jahren</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="border-t border-line bg-ember" data-testid="home-cta">
    <div className="mx-auto flex max-w-[1600px] flex-col gap-10 px-6 py-24 md:flex-row md:items-center md:justify-between md:px-12 md:py-32">
      <Reveal>
        <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tighter text-ink md:text-6xl">
          Ihr Projekt.<br />Unser Fundament.
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <Link
          to="/kontakt"
          data-testid="cta-contact-button"
          className="group inline-flex items-center gap-4 border-2 border-ink px-10 py-5 font-mono text-sm uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-ink hover:text-ember"
        >
          Jetzt anfragen
          <ArrowRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <ServicesBento />
      <ProjectsTeaser />
      <Marquee reverse outline={false} />
      <AboutTeaser />
      <CTASection />
    </main>
  );
}
